// בלוקים ו-Screen (09 §1) — משותפים ל-09/10/11.
import { z } from 'zod';
import { Row } from './common.ts';

export const ColType = z.enum(['text', 'number', 'currency', 'percent', 'date', 'datetime', 'id']);
export type ColType = z.infer<typeof ColType>;

export const Col = z.object({
  key: z.string(),
  labelHe: z.string().max(200),
  type: ColType,
  align: z.enum(['start', 'end']).optional(),
  entity: z.string().optional(),
});
export type Col = z.infer<typeof Col>;

const Title = z.string().max(200).optional();

/**
 * טבלה (11 §1). המיון בלחיצה על כותרת, החיפוש והסינון לפי עמודה — אוטומטיים בתצוגה (חיפוש וסינון מ-10 שורות).
 * רמזים אופציונליים: sort = המיון הראשון; filters:false = בלי חיפוש/סינון (true = גם בטבלה קטנה); totals = שורת סה"כ
 * (סכום עמודות number/currency של השורות שבתצוגה — אחרי סינון).
 */
export const TableBlock = z
  .object({
    type: z.literal('table'),
    id: z.string(),
    titleHe: Title,
    columns: z.array(Col).max(60),
    rows: z.array(Row).max(5000),
    truncated: z.boolean().optional(),
    totalRows: z.number().int().nonnegative().optional(),
    sort: z.object({ key: z.string(), dir: z.enum(['asc', 'desc']) }).optional(),
    filters: z.boolean().optional(),
    totals: z.boolean().optional(),
  })
  .superRefine((b, ctx) => {
    if (b.sort && !b.columns.some((c) => c.key === b.sort!.key))
      ctx.addIssue({ code: 'custom', path: ['sort', 'key'], message: `sort.key "${b.sort.key}" אינו עמודה (${b.columns.map((c) => c.key).join(', ')})` });
  });
export type TableBlock = z.infer<typeof TableBlock>;

export const ChartDash = z.enum(['solid', 'dashed', 'dotted']);
export type ChartDash = z.infer<typeof ChartDash>;

/**
 * עיצוב לסדרה אחת בגרף (09 §1). `key` חייב להיות ב-`y`. `color`: hex (#0066FF) או אינדקס בפלטת המותג ('1'..'6').
 * `group`/`variant`: כל הסדרות של אותה קבוצה חולקות גוון; ה-variant קובע את הקו — הראשון רציף, השני מקווקו, השלישי מנוקד
 * (סוכן × שנה: group=סוכן, variant=שנה — בלי צבעים ידניים). color/dash מפורשים גוברים.
 */
export const ChartSeries = z.strictObject(
  {
    key: z.string(),
    labelHe: z.string().max(200).optional(),
    color: z.string().regex(/^(#[0-9a-fA-F]{6}|[1-9]|1[0-2]|muted)$/, 'color: hex כמו #0066FF, אינדקס פלטה 1 עד 12, או muted (אפור)').optional(),
    dash: ChartDash.optional(),
    group: z.string().max(200).optional(),
    variant: z.string().max(200).optional(),
  },
  { error: (iss) => (iss.code === 'unrecognized_keys' ? `מאפיינים לא מוכרים בסדרה: ${iss.keys.join(', ')}. המותר: key, labelHe, color, dash, group, variant` : undefined) },
);
export type ChartSeries = z.infer<typeof ChartSeries>;

export const CHART_PROPS = ['type', 'id', 'titleHe', 'chartType', 'x', 'y', 'rows', 'unit', 'stacked', 'series', 'seriesBy', 'top', 'show', 'highlight', 'marks', 'stats'] as const;

/** סטטיסטיקה לסדרה בטבלת המקרא של הגרף: total, share (מהסך), avg, last (נקודה אחרונה), change (אחרונה מול הקודמת, שבר). */
export const ChartStat = z.enum(['total', 'share', 'avg', 'last', 'change']);
export type ChartStat = z.infer<typeof ChartStat>;

/** הערה על הגרף (2026-10-13): קו אנכי ב-x, או נקודה על סדרה אחת כש-series נתון. */
export const ChartMark = z.strictObject({ x: z.union([z.string(), z.number()]), labelHe: z.string().max(80), series: z.string().optional() });
export type ChartMark = z.infer<typeof ChartMark>;

/** מאפיין לא מוכר → שגיאה (לא נבלע בשקט): המודל מקבל את רשימת המותר. */
export const ChartBlock = z
  .strictObject(
    {
      type: z.literal('chart'),
      id: z.string(),
      titleHe: Title,
      chartType: z.enum(['bar', 'line', 'area', 'pie']),
      x: z.string(),
      y: z.array(z.string()).min(1),
      rows: z.array(Row).max(5000),
      unit: z.enum(['number', 'currency', 'percent']).optional(),
      stacked: z.boolean().optional(),
      series: z.array(ChartSeries).max(60).optional(),
      /** פורמט ארוך: עמודה שערכיה הם שמות הסדרות (קבוצה × חודש); הערך = y[0]. series[].key/show/highlight = שמות הסדרות. */
      seriesBy: z.string().optional(),
      /** מצב חוקר (סדרות רבות): כמה מובילות מוצגות בהתחלה. */
      top: z.number().int().min(1).max(30).optional(),
      /** אילו סדרות מוצגות בהתחלה (גובר על top). */
      show: z.array(z.string()).max(30).optional(),
      /** סדרות מודגשות (קו עבה, השאר רגילות). */
      highlight: z.array(z.string()).max(12).optional(),
      marks: z.array(ChartMark).max(20).optional(),
      stats: z.array(ChartStat).max(5).optional(),
    },
    { error: (iss) => (iss.code === 'unrecognized_keys' ? `מאפיינים לא מוכרים בבלוק chart: ${iss.keys.join(', ')}. המותר: ${CHART_PROPS.join(', ')}` : undefined) },
  )
  .superRefine((b, ctx) => {
    const seen = new Set<string>();
    for (const [i, s] of (b.series ?? []).entries()) {
      // seriesBy: המפתח הוא שם סדרה מתוך הנתונים, לא עמודה ב-y
      if (!b.seriesBy && !b.y.includes(s.key)) ctx.addIssue({ code: 'custom', path: ['series', i, 'key'], message: `series[${i}].key "${s.key}" אינו ב-y (${b.y.join(', ')})` });
      if (seen.has(s.key)) ctx.addIssue({ code: 'custom', path: ['series', i, 'key'], message: `series[${i}].key "${s.key}" מופיע פעמיים` });
      seen.add(s.key);
    }
  });
export type ChartBlock = z.infer<typeof ChartBlock>;

export const KpiBlock = z.object({
  type: z.literal('kpi'),
  id: z.string(),
  titleHe: Title,
  cards: z.array(
    z.object({
      labelHe: z.string(),
      value: z.union([z.number(), z.string(), z.null()]),
      type: ColType,
      delta: z.number().nullable().optional(),
      hintHe: z.string().optional(),
      /** קו מגמה קטן (≤ 60 נקודות, מהישנה לחדשה). */
      spark: z.array(z.number().nullable()).max(60).optional(),
      /** איזה כיוון טוב: down = ירידה בירוק (הוצאות, חובות). ברירת מחדל up. */
      good: z.enum(['up', 'down']).optional(),
    }),
  ),
});
export type KpiBlock = z.infer<typeof KpiBlock>;

export const TextBlock = z.object({ type: z.literal('text'), id: z.string(), titleHe: Title, markdown: z.string() });
export type TextBlock = z.infer<typeof TextBlock>;

/** iframe sandbox, בלי רשת; ≤ 512KB. */
export const HtmlBlock = z.object({
  type: z.literal('html'),
  id: z.string(),
  titleHe: Title,
  html: z.string().max(512 * 1024),
  height: z.number().int().positive().optional(),
});
export type HtmlBlock = z.infer<typeof HtmlBlock>;

/**
 * אפליקציה (10, 2026-10-13): HTML + JS או אפליקציית React (tsx) שאייבי כותב — רץ בדפדפן ב-iframe sandbox="allow-scripts" בלי allow-same-origin
 * (origin אטום: אין גישה לעוגיות, ל-storage או לדף האם). data מוזרק כ-ab.data; אבני בניין (ab.chart/ab.table/ab.fmt,
 * tokens של העיצוב) וספריות מ-https זמינות לו. בניגוד ל-html (סטטי לגמרי).
 */
export const AppBlock = z.object({
  type: z.literal('app'),
  id: z.string(),
  titleHe: Title,
  /** html שאייבי כתב ≤ 512KB (נאכף בשמירת הפלט); ביצירת app (22 §1.4) — ה-dist שהפלטפורמה בנתה, ≤ 2MB. */
  html: z.string().max(2 * 1024 * 1024 + 64 * 1024).optional(),
  /** אפליקציית React (TSX) שאייבי כתב — המקור; js = התוצר המקומפל (ממלא השרת, לא אייבי). */
  tsx: z.string().max(512 * 1024).optional(),
  js: z.string().max(1024 * 1024).optional(),
  data: z.unknown().optional(),
  height: z.number().int().positive().optional(),
  /** 16: הריצה שבנתה את הבלוק — ה-api מציב בשמירת ה-Screen (לא הקוד); ab.call הולך ל-POST /api/runs/:runId/calls/:action. */
  source: z.object({ runId: z.uuid() }).optional(),
});
export type AppBlock = z.infer<typeof AppBlock>;

export const FileBlock = z.object({
  type: z.literal('file'),
  id: z.string(),
  titleHe: Title,
  file: z.object({ id: z.string(), name: z.string(), mime: z.string(), bytes: z.number().int().nonnegative() }),
});
export type FileBlock = z.infer<typeof FileBlock>;

export const Block = z.discriminatedUnion('type', [TableBlock, ChartBlock, KpiBlock, TextBlock, HtmlBlock, AppBlock, FileBlock]);
export type Block = z.infer<typeof Block>;

export const NoticeKind = z.enum(['info', 'degraded', 'error', 'unsupported']);
export type NoticeKind = z.infer<typeof NoticeKind>;

export const Notice = z.object({ kind: NoticeKind, textHe: z.string() });
export type Notice = z.infer<typeof Notice>;

/**
 * תור ביצירת `chat` (09 §4): השאלה והטופס שבינה-3 בנתה (null כשלא נבנה טופס שרץ). המסך שולח את הזוגות האלה
 * כ-history בהודעה הבאה (≤ 5) — היצירה עצמה בלי מצב בין ריצות.
 */
export const ChatTurn = z.object({ question: z.string().max(2000), form: z.record(z.string(), z.unknown()).nullable() });
export type ChatTurn = z.infer<typeof ChatTurn>;

/** פלט של יצירה. asOf = min על כל data.query שבוצעו. */
export const Screen = z.object({
  blocks: z.array(Block),
  asOf: z.string().nullable(),
  notices: z.array(Notice),
  turn: ChatTurn.optional(),
});
export type Screen = z.infer<typeof Screen>;
